import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { GroupData } from "../../../types/GroupData";
import { useGroups } from "../../../hooks/useGroups";
import { useSummary } from "../../../hooks/useSummary";
import { useResultPages } from "../../../hooks/useResultPages";
import YearPager from "./common/YearPager";
import JudgingSheetDownloadButton from "./common/JudgingSheetDownloadButton/JudgingSheetDownloadButton";
import JudgingSheetPreviewButton from "./common/JudgingSheetDownloadButton/JudgingSheetPreviewButton";
import JudgingSheetPreviewButtonShortForm from "./common/JudgingSheetDownloadButton/JudgingSheetPreviewButtonShortForm";
import {
  getEntryClassLabel,
  formatDateFromTimestamp,
} from "../../../utils/judgingSheetUtils";
import "../../../assets/styles/common/listCard.scss";
import "../../../assets/styles/judgeSheet.scss";

interface SheetSection {
  key: string;
  label: string;
  groups: GroupData[];
  // シートに印字する日付（基本情報に登録されている、このクラスの開催日）
  dateStr: string;
}

const JudgingSheetLists: React.FC = () => {
  const { groups } = useGroups();
  const { summaries } = useSummary();
  const { resultPages } = useResultPages();

  const [currentYear, setCurrentYear] = useState(
    () => Number(localStorage.getItem("currentYear")) || 2024,
  );

  // セクションごとのエントリー一覧（アコーディオン）の開閉状態。初期値はすべて閉じておく
  const [openSections, setOpenSections] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(
    () => localStorage.setItem("currentYear", String(currentYear)),
    [currentYear],
  );

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 基本情報（Summary）に登録されたその年のJudge Panelを取得
  const currentSummary = summaries.find(
    (summary) => summary.id === String(currentYear),
  );

  const judgePanel = {
    MUS: currentSummary?.judgePanelMUS ?? "",
    PER: currentSummary?.judgePanelPER ?? "",
    SNG: currentSummary?.judgePanelSNG ?? "",
  };

  // 「点数」ページのResults page設定から、その年のFinalist Countを取得
  const currentResultPage = resultPages.find(
    (page) => Number(page.year) === currentYear,
  );
  const finalistCount = currentResultPage?.finalistCount ?? 0;

  const getGroupsForClass = (entryClass: string) =>
    groups
      .filter((group) => group.isDeleted !== true)
      .filter((group) => group.entryClass === entryClass)
      .filter((group) => {
        const createdAt = group.createdAt?.toDate();
        return createdAt?.getFullYear() === currentYear;
      })
      .sort(
        (a, b) => (Number(a.entryNumber) || 0) - (Number(b.entryNumber) || 0),
      );

  // Final は finalOrder 順に並べ、finalistCount の件数までに絞り込む。
  // 表示・PDF上の番号も entryNumber ではなく finalOrder を使うよう置き換える。
  const getFinalGroups = (classGroups: GroupData[]) =>
    [...classGroups]
      .sort((a, b) => (a.finalOrder ?? Infinity) - (b.finalOrder ?? Infinity))
      .slice(0, finalistCount)
      .map((group) => ({ ...group, entryNumber: group.finalOrder }));

  const quartetGroups = getGroupsForClass("QuartetContest");
  const quartetFinalGroups = getFinalGroups(quartetGroups);

  // Quartet Contest Final：単独のPDF（オリジナルのテンプレート画像）＋専用ダウンロードボタン
  const quartetFinalSection: SheetSection = {
    key: "QuartetContest-final",
    label: `${getEntryClassLabel("QuartetContest")} Final`,
    groups: quartetFinalGroups,
    dateStr: formatDateFromTimestamp(currentSummary?.quartetFinalAt),
  };

  // Quartet Semifinal・Chorus・Evaluation：3クラスまとめて1つのダウンロードボタン
  // （テンプレート画像はFinalと同じオリジナル画像を使用）。アコーディオンとしては
  // クラスごとに開閉・件数表示は分けたまま表示する。
  const combinedSections: SheetSection[] = [
    {
      key: "QuartetContest-semiFinal",
      label: `${getEntryClassLabel("QuartetContest")} Semi Final`,
      groups: quartetGroups,
      dateStr: formatDateFromTimestamp(currentSummary?.quartetSemiFinalAt),
    },
    {
      key: "ChorusContest",
      label: getEntryClassLabel("ChorusContest"),
      groups: getGroupsForClass("ChorusContest"),
      dateStr: formatDateFromTimestamp(currentSummary?.chorusFinalAt),
    },
    {
      key: "EvaluationOnly",
      label: getEntryClassLabel("EvaluationOnly"),
      groups: getGroupsForClass("EvaluationOnly"),
      dateStr: formatDateFromTimestamp(currentSummary?.evaluationOnlyAt),
    },
  ];

  // Short form（Musicality/Performance/Singing_Judging_short_Form.png）用に、
  // 同じ2クラス構成（Quartet Contest Final／Quartet Semifinal・Chorus・Evaluation）を
  // もう1セット用意する。openSectionsのキーが重複しないよう、末尾に専用のsuffixを付ける。
  const SHORT_FORM_KEY_SUFFIX = "-shortForm";

  const quartetFinalSectionShortForm: SheetSection = {
    ...quartetFinalSection,
    key: `${quartetFinalSection.key}${SHORT_FORM_KEY_SUFFIX}`,
  };

  const combinedSectionsShortForm: SheetSection[] = combinedSections.map(
    (section) => ({
      ...section,
      key: `${section.key}${SHORT_FORM_KEY_SUFFIX}`,
    }),
  );

  const renderClassSection = (
    section: SheetSection,
    options?: { style?: React.CSSProperties; actions?: React.ReactNode },
  ) => {
    const isOpen = !!openSections[section.key];

    return (
      <section
        className="judging-sheet-class-section"
        key={section.key}
        style={options?.style}
      >
        <div className="flex-space-between judging-sheet-class-header">
          <div
            className="judging-sheet-class-list"
            role="button"
            tabIndex={0}
            onClick={() => toggleSection(section.key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleSection(section.key);
              }
            }}
            title="クリックでエントリー一覧を開閉"
          >
            <div className="flex-align-center" style={{ gap: "10px" }}>
              {isOpen ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
              <span className="judging-sheet-entry-count-text">
                {section.groups.length}件
              </span>
              <h2>{section.label}</h2>
            </div>
            {options?.actions && (
              <div className="judging-sheet-actions">{options.actions}</div>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="judging-sheet-group-list">
            {section.groups.length === 0 ? (
              <p className="judging-sheet-empty">
                エントリーがありません。
              </p>
            ) : (
              section.groups.map((group) => (
                <div
                  className="judging-sheet-group-row flex-align-center"
                  key={group.id}
                >
                  <p className="entry-number">{group.entryNumber}</p>
                  <span>{group.groupName}</span>
                </div>
              ))
            )}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="table-contents">
      <div className="table-main">
        <div className="table-header">
          <YearPager
            currentYear={currentYear}
            minYear={2024}
            maxYear={new Date().getFullYear()}
            setCurrentYear={setCurrentYear}
          />

          <div className="judging-sheet-actions">
            <JudgingSheetPreviewButton />
          </div>
        </div>

        <div className="judge-area">
          <div className="judge-panel">
            <h3>Judge Panel</h3>
            <div>
              <p>
                <span>MUS：</span>
                {judgePanel.MUS || "未設定"}
              </p>
              <p>
                <span>PER：</span>
                {judgePanel.PER || "未設定"}
              </p>
              <p>
                <span>SNG：</span>
                {judgePanel.SNG || "未設定"}
              </p>
            </div>
          </div>
        </div>
        <div className="judging-sheet-list-area">
          {/* Quartet Contest Final：単独ダウンロード（オリジナルのテンプレート画像） */}
          {renderClassSection(quartetFinalSection, {
            actions: (
              <JudgingSheetDownloadButton
                sections={[
                  {
                    entryClassLabel: quartetFinalSection.label,
                    dateStr: quartetFinalSection.dateStr,
                    groups: quartetFinalSection.groups,
                  },
                ]}
                judgePanel={judgePanel}
                fileNamePrefix={`${currentYear}_QuartetContest_Final`}
                buttonTitle={`${quartetFinalSection.label} 全エントリーの採点用紙をまとめてダウンロード`}
              />
            ),
          })}

          {/* Quartet Semifinal・Chorus・Evaluation：3クラスまとめて1つのダウンロードボタン */}
          <div
            className="flex-space-between"
            style={{
              alignItems: "center",
              marginTop: "26px",
              paddingTop: "14px",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <h2 style={{ width: "auto" }}>
              Quartet Semifinal・Chorus・Evaluation
            </h2>
            <div className="judging-sheet-actions">
              <JudgingSheetDownloadButton
                sections={combinedSections.map((section) => ({
                  entryClassLabel: section.label,
                  dateStr: section.dateStr,
                  groups: section.groups,
                }))}
                judgePanel={judgePanel}
                fileNamePrefix={`${currentYear}_QuartetSemiFinal_Chorus_Evaluation`}
                buttonTitle="Quartet Semifinal・Chorus・Evaluation 全エントリーの採点用紙をまとめてダウンロード"
              />
            </div>
          </div>

          {combinedSections.map((section, index) =>
            renderClassSection(
              section,
              // 直前の共通ヘッダーが区切り線を兼ねているため、
              // 3クラス中最初の1つだけ自身の区切り線・上マージンを打ち消す
              index === 0
                ? { style: { marginTop: 0, paddingTop: 0, borderTop: "none" } }
                : undefined,
            ),
          )}
        </div>

        {/* Short form（Musicality/Performance/Singing_Judging_short_Form.png）用のダウンロード */}
        <div
          className="flex-space-between"
          style={{ alignItems: "center", margin: "20px 20px 0" }}
        >
          <h2 style={{ width: "auto" }}>Short Form</h2>
          <div className="judging-sheet-actions">
            <JudgingSheetPreviewButtonShortForm />
          </div>
        </div>
        <div className="judging-sheet-list-area">
          {renderClassSection(quartetFinalSectionShortForm, {
            actions: (
              <JudgingSheetDownloadButton
                sections={[
                  {
                    entryClassLabel: quartetFinalSectionShortForm.label,
                    dateStr: quartetFinalSectionShortForm.dateStr,
                    groups: quartetFinalSectionShortForm.groups,
                  },
                ]}
                judgePanel={judgePanel}
                fileNamePrefix={`${currentYear}_QuartetContest_Final_ShortForm`}
                buttonTitle={`${quartetFinalSectionShortForm.label}（Short Form） 全エントリーの採点用紙をまとめてダウンロード`}
                useShortForm
              />
            ),
          })}

          <div
            className="flex-space-between"
            style={{
              alignItems: "center",
              marginTop: "26px",
              paddingTop: "14px",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <h2 style={{ width: "auto" }}>
              Quartet Semifinal・Chorus・Evaluation
            </h2>
            <div className="judging-sheet-actions">
              <JudgingSheetDownloadButton
                sections={combinedSectionsShortForm.map((section) => ({
                  entryClassLabel: section.label,
                  dateStr: section.dateStr,
                  groups: section.groups,
                }))}
                judgePanel={judgePanel}
                fileNamePrefix={`${currentYear}_QuartetSemiFinal_Chorus_Evaluation_ShortForm`}
                buttonTitle="Quartet Semifinal・Chorus・Evaluation（Short Form） 全エントリーの採点用紙をまとめてダウンロード"
                useShortForm
              />
            </div>
          </div>

          {combinedSectionsShortForm.map((section, index) =>
            renderClassSection(
              section,
              index === 0
                ? { style: { marginTop: 0, paddingTop: 0, borderTop: "none" } }
                : undefined,
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default JudgingSheetLists;
